var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var DEFAULT_AUTHORITY_LEADER = '00000nz  a2200000n  4500';
var isControlField = function (tag) { return tag.length === 3 && tag < '010'; };
/**
 * CZNA-1506 — one subfield on the wire, with the row's FK when it has one. A 5XX link picked
 * in the register carries its target only as the FK of its `$a` row, and the save stamps
 * `$7` from it; without the FK the save preflight judged a record missing the `$7` the
 * stored one has (ALEPH_6116). The key is left out when there is no FK.
 */
var toSubfield = function (code, value, sourceAuthorityId) { return (sourceAuthorityId ? { code: code, value: value, sourceAuthorityId: sourceAuthorityId } : { code: code, value: value }); };
/**
 * Project-internal `RecordDataField` rows → MARC `MarcRecord` payload.
 * Sorts the fields by (tag, fieldIndex) so the resulting payload is stable
 * across invocations (avoids spurious diffs when comparing before/after
 * recheck results).
 */
export var recordToMarc = function (dataFields, leader) {
    if (leader === void 0) { leader = DEFAULT_AUTHORITY_LEADER; }
    // Group rows by (tag, fieldIndex). The subfield rows are already inside
    // each field (subfields: RecordDataSubfield[]); we just need to translate
    // each field into the MARC shape.
    var sorted = __spreadArray([], dataFields, true).sort(function (a, b) {
        var _a, _b;
        if (a.fieldTag !== b.fieldTag)
            return a.fieldTag.localeCompare(b.fieldTag);
        return ((_a = a.fieldIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.fieldIndex) !== null && _b !== void 0 ? _b : 0);
    });
    var fields = sorted.map(function (rdf) {
        var _a, _b, _c, _d;
        if (isControlField(rdf.fieldTag)) {
            // Control fields concatenate all subfield values (typically only $a).
            var data = ((_a = rdf.subfields) !== null && _a !== void 0 ? _a : []).map(function (s) { return s.value; }).join('');
            return { tag: rdf.fieldTag, data: data };
        }
        // CZNA-1382: in the order the backend returns them, which is the stored
        // order. `subfieldIndex` counts within one code only — sorting by it sent
        // `111 $a $n $n $t` out as `$a $n $t $n`.
        var subfields = ((_b = rdf.subfields) !== null && _b !== void 0 ? _b : [])
            .map(function (s) { return toSubfield(s.subfieldCode, s.value, s.sourceAuthorityId); });
        return {
            tag: rdf.fieldTag,
            indicator1: (_c = rdf.indicator1) !== null && _c !== void 0 ? _c : ' ',
            indicator2: (_d = rdf.indicator2) !== null && _d !== void 0 ? _d : ' ',
            subfields: subfields,
        };
    });
    return { leader: leader, fields: fields };
};
/**
 * Flat `ProposalDataField[]` (one row per subfield) → MARC payload.
 * Proposal rows are the form/save-time shape (snake_case); group by
 * (field_tag, field_index) into one MarcField each.
 */
/* eslint-disable camelcase -- matches backend snake_case field names */
export var proposalDataToMarc = function (proposalData, leader) {
    if (leader === void 0) { leader = DEFAULT_AUTHORITY_LEADER; }
    var byField = new Map();
    for (var _i = 0, proposalData_1 = proposalData; _i < proposalData_1.length; _i++) {
        var row = proposalData_1[_i];
        var key = "".concat(row.field_tag, "#").concat(row.field_index);
        var bucket = byField.get(key);
        if (!bucket) {
            bucket = {
                tag: row.field_tag,
                ind1: row.indicator1,
                ind2: row.indicator2,
                rows: [],
            };
            byField.set(key, bucket);
        }
        else {
            // First non-empty indicator wins; the form always writes the same value
            // for all subfield rows of one field, but be defensive.
            if (!bucket.ind1 && row.indicator1)
                bucket.ind1 = row.indicator1;
            if (!bucket.ind2 && row.indicator2)
                bucket.ind2 = row.indicator2;
        }
        bucket.rows.push(row);
    }
    var fields = Array.from(byField.values())
        .sort(function (a, b) { return a.tag.localeCompare(b.tag); })
        .map(function (bucket) {
        var _a, _b;
        if (isControlField(bucket.tag)) {
            var data = bucket.rows
                .slice()
                .sort(function (a, b) { var _a, _b; return ((_a = a.subfield_index) !== null && _a !== void 0 ? _a : 0) - ((_b = b.subfield_index) !== null && _b !== void 0 ? _b : 0); })
                .map(function (r) { return r.value; })
                .join('');
            return { tag: bucket.tag, data: data };
        }
        // CZNA-1382: row order is subfield order — see `recordToMarc`.
        var subfields = bucket.rows
            .filter(function (r) { return !!r.subfield_code; })
            .map(function (r) { return toSubfield(r.subfield_code, r.value, r.source_authority_id); });
        return {
            tag: bucket.tag,
            indicator1: (_a = bucket.ind1) !== null && _a !== void 0 ? _a : ' ',
            indicator2: (_b = bucket.ind2) !== null && _b !== void 0 ? _b : ' ',
            subfields: subfields,
        };
    });
    return { leader: leader, fields: fields };
};
/* eslint-enable camelcase */
